import {
  AlertIcon,
  ArchiveIcon,
  BeakerIcon,
  BookmarkIcon,
  BriefcaseIcon,
  BugIcon,
  CalendarIcon,
  ClockIcon,
  CodeReviewIcon,
  CodescanIcon,
  CommentDiscussionIcon,
  CommentIcon,
  CopilotIcon,
  DependabotIcon,
  EyeIcon,
  FileDiffIcon,
  FlameIcon,
  GearIcon,
  GitPullRequestIcon,
  GlobeIcon,
  HomeIcon,
  HubotIcon,
  InboxIcon,
  IssueOpenedIcon,
  LinkIcon,
  MentionIcon,
  MeterIcon,
  MoonIcon,
  NorthStarIcon,
  OrganizationIcon,
  PeopleIcon,
  PersonIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  RepoTemplateIcon,
  RocketIcon,
  SmileyIcon,
  SquirrelIcon,
  SunIcon,
  TelescopeIcon,
  TerminalIcon,
  ToolsIcon,
  ZapIcon,
} from '@primer/octicons-react'

export interface UserShortcut {
  /**
   * The numeric database id. Used as the path segment for
   * `PUT`/`DELETE /users/shortcuts/:id`, which the server resolves with
   * `SearchShortcut.find_by(id:)`, so it must not be swapped for `globalId`.
   */
  id: string
  /**
   * The GraphQL global id. Canonical identifier for saved-view routes
   * (`/issues/SSC_kgDOAAi2Mw`). The exact shape is a backend detail — dotcom sends
   * `SSC_…`, GHES sends a legacy base64 relay id — so never match it by shape.
   * Optional because older backends omit it; read it through `savedViewRouteId` so
   * numeric ids keep resolving.
   */
  globalId?: string
  name: string
  searchType: 'repositories' | 'issues' | 'pull-requests'
  query: string
  description?: string
  icon: IconName
  color: IconColor
}

export type SavedViewVisibility = 'team' | 'personal'

export interface SavedView {
  id: number
  name: string
  description: string | null
  icon: IconName
  color: IconColor
  query: string
  position: number | null
  pinned?: boolean
  visibility?: SavedViewVisibility
  createdAt: string
  updatedAt: string
  creatorLogin: string | null
}

export interface SavedViewsResponse {
  savedViews: SavedView[]
  viewerCanManageViews: boolean
}

export interface SidebarLink extends UserShortcut {
  source: 'predefined' | 'user'
  path?: string
}

const defaultIcon: IconName = 'bookmark'
const defaultColor: IconColor = 'gray'

export function getIconColorClassName(color: string | undefined) {
  const colorEntry = colorMap[color?.toLowerCase() as IconColor] || colorMap[defaultColor]
  return colorEntry.foreground
}

export function getBackgroundIconColorClassName(color: string | undefined) {
  const colorEntry = colorMap[color?.toLowerCase() as IconColor] || colorMap[defaultColor]
  return colorEntry.background
}

export function getIconComponent(name: string | undefined) {
  return iconMap[name?.toLowerCase() as IconName] || iconMap[defaultIcon]
}

export function getDisplayName(name: string) {
  return `${name.charAt(0).toUpperCase() + name.slice(1).replaceAll('_', ' ')}`
}

const colorMap = {
  ['gray']: {foreground: 'color-fg-muted', background: 'color-bg-emphasis'},
  ['blue']: {foreground: 'color-fg-accent', background: 'color-bg-accent-emphasis'},
  ['green']: {foreground: 'color-fg-success', background: 'color-bg-success-emphasis'},
  ['orange']: {foreground: 'color-fg-severe', background: 'color-bg-severe-emphasis'},
  ['red']: {foreground: 'color-fg-danger', background: 'color-bg-danger-emphasis'},
  ['pink']: {foreground: 'color-fg-sponsors', background: 'color-bg-sponsors-emphasis'},
  ['purple']: {foreground: 'color-fg-done', background: 'color-bg-done-emphasis'},
} as const

export type IconColor = keyof typeof colorMap

export const supportedIconColors = Object.keys(colorMap) as IconColor[]

const iconMap = {
  ['people']: PeopleIcon,
  ['smiley']: SmileyIcon,
  ['mention']: MentionIcon,
  ['clock']: ClockIcon,
  ['git_pull_request']: GitPullRequestIcon,
  ['zap']: ZapIcon,
  ['issue_opened']: IssueOpenedIcon,
  ['person']: PersonIcon,
  ['comment_discussion']: CommentDiscussionIcon,
  ['organization']: OrganizationIcon,
  ['briefcase']: BriefcaseIcon,
  ['file_diff']: FileDiffIcon,
  ['code_review']: CodeReviewIcon,
  ['codescan']: CodescanIcon,
  ['terminal']: TerminalIcon,
  ['tools']: ToolsIcon,
  ['beaker']: BeakerIcon,
  ['alert']: AlertIcon,
  ['eye']: EyeIcon,
  ['telescope']: TelescopeIcon,
  ['bookmark']: BookmarkIcon,
  ['calendar']: CalendarIcon,
  ['meter']: MeterIcon,
  ['moon']: MoonIcon,
  ['sun']: SunIcon,
  ['flame']: FlameIcon,
  ['bug']: BugIcon,
  ['north_star']: NorthStarIcon,
  ['rocket']: RocketIcon,
  ['squirrel']: SquirrelIcon,
  ['hubot']: HubotIcon,
  ['dependabot']: DependabotIcon,
  ['repo']: RepoIcon,
  ['repo_locked']: RepoLockedIcon,
  ['link']: LinkIcon,
  ['repo_forked']: RepoForkedIcon,
  ['repo_template']: RepoTemplateIcon,
  ['archive']: ArchiveIcon,
  ['home']: HomeIcon,
  ['inbox']: InboxIcon,
  ['gear']: GearIcon,
  ['copilot']: CopilotIcon,
  ['globe']: GlobeIcon,
  ['comment']: CommentIcon,
} as const

export type IconName = keyof typeof iconMap

export const supportedIconNames = Object.keys(iconMap) as IconName[]
